import { Link } from 'react-router-dom';
import { useState } from 'react';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim()) {
      setStatus('submitted');
      setEmail('');
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }

    setStatus('submitting');

    try {
      const urlEncoded = new URLSearchParams();
      urlEncoded.append('email', email.trim());
      formData.forEach((value, key) => {
        if (key !== 'website_alt' && key !== 'email') {
          urlEncoded.append(key, value as string);
        }
      });

      await fetch('https://readdy.ai/api/form/d915kf04jh3v1cgksrk0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      setStatus('submitted');
      setEmail('');
      setTimeout(() => setStatus('idle'), 3000);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <footer className="py-16 md:py-20 bg-background-50 border-t border-background-200/40">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="lg:w-2/5">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
              Stay Updated<br />With New Finds
            </h3>
            <p className="mt-3 text-sm text-foreground-500 leading-relaxed">
              Get notified when we add fresh spreadsheet items.
            </p>

            <form onSubmit={handleSubmit} data-readdy-form="Newsletter Signup" className="mt-6 space-y-3">
              <div>
                <label htmlFor="footer-email" className="text-xs font-medium text-foreground-500 mb-1 block">
                  Email address
                </label>
                <input
                  type="email"
                  id="footer-email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="w-full bg-transparent border-0 border-b border-foreground-300 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-700 transition-colors"
                />
              </div>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="px-6 py-2.5 border border-foreground-300 rounded-full text-sm font-medium text-foreground-700 hover:border-foreground-700 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? 'Subscribing...' : status === 'submitted' ? 'Subscribed!' : status === 'error' ? 'Try Again' : 'Subscribe'}
              </button>
            </form>
          </div>

          <div className="lg:w-3/5">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-xs font-semibold tracking-widest uppercase text-foreground-400 mb-4">Main Site</h4>
                <ul className="space-y-2.5">
                  <li>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      pandabuy sheet
                    </a>
                  </li>
                  <li>
                    <Link
                      to="/"
                      className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/article/pandabuy-spreadsheet-guide"
                      className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      SEO Guide
                    </Link>
                  </li>
                </ul>
              </div>

              {[0, 7].map((startIdx) => (
                <div key={startIdx}>
                  <h4 className="text-xs font-semibold tracking-widest uppercase text-foreground-400 mb-4">
                    {startIdx === 0 ? 'Categories' : 'More'}
                  </h4>
                  <ul className="space-y-2.5">
                    {categories.slice(startIdx, startIdx + 7).map((cat) => (
                      <li key={cat.name}>
                        <a
                          href={cat.link}
                          target="_blank"
                          rel="noopener nofollow"
                          className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {cat.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-background-200/40 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-400">
            &copy; {new Date().getFullYear()} pandabuy.cfd — All external links open at kaspreadsheet.com
          </p>
          <p className="text-xs text-foreground-400">
            Curated pandabuyspreadsheet for global fashion shoppers
          </p>
        </div>
      </div>
    </footer>
  );
}